import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg' | 'icon';

const VARIANTS: Record<Variant, string> = {
  // theme.md: solid brand red, darkening to --brand-hover. Not brand-700, which dark mode
  // lightens into an ink colour.
  primary: 'bg-brand-600 text-white hover:bg-brand-hover active:bg-brand-hover',
  secondary: 'bg-slate-100 text-slate-900 hover:bg-slate-200 active:bg-slate-300',
  outline: 'border border-slate-300 bg-surface text-slate-900 hover:bg-slate-50',
  ghost: 'text-slate-700 hover:bg-slate-100',
  // theme.md destructive: #ef4444, hovering to #dc2626.
  danger: 'bg-[#ef4444] text-white hover:bg-[#dc2626] active:bg-[#dc2626]',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm',
  // 40px tall: comfortably above the 44px-with-padding touch target guidance on mobile.
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-6 text-base',
  icon: 'h-10 w-10',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  /** Render as the child element (e.g. a router Link) while keeping button styling. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', isLoading, disabled, children, asChild, ...props },
  ref,
) {
  const Component = asChild ? Slot : 'button';

  return (
    <Component
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      disabled={asChild ? undefined : disabled || isLoading}
      // Communicates the busy state to assistive technology, not just visually.
      aria-busy={isLoading || undefined}
      {...props}
    >
      {/*
        Slot requires exactly one React element child — it clones that element and merges
        props onto it. Wrapping the child alongside a spinner hands it an array and throws.
        A link rendered via asChild has no busy state anyway, so it passes straight through.
      */}
      {asChild ? (
        children
      ) : (
        <>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {children}
        </>
      )}
    </Component>
  );
});
